import { createContext, useEffect, useState } from 'react';
import { verifyToken } from 'utils/jwt';
import axios, { forceRefreshToken } from 'utils/axios';
import { getToken, setToken, clearToken } from 'utils/tokenManager';
import useTelegramToken from 'hooks/useTelegramToken';
import { Box, Typography, Button, Paper } from '@mui/material';
import Loader from 'components/Loader';

const initialAuthState = {
  isTokenValid: false,
  loading: true,
  error: false
};

/**
 * Set session with token in memory (primary) and Authorization header
 * Backend will also set HttpOnly cookie automatically
 */
const setSession = (token) => {
  if (token) {
    setToken(token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    clearToken();
    delete axios.defaults.headers.common.Authorization;
  }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [authState, setAuthState] = useState(initialAuthState);
  const { responseData } = useTelegramToken();
  const telegramToken = responseData?.token ?? null;

  const initToken = async () => {
    try {
      setAuthState({ isTokenValid: false, loading: true, error: false });

      // Check if we have a valid token in memory
      let token = getToken();
      if (token && verifyToken(token)) {
        setSession(token);
        setAuthState({ isTokenValid: true, loading: false, error: false });
        return;
      }

      // Try telegram token
      if (telegramToken && verifyToken(telegramToken)) {
        setSession(telegramToken);
        setAuthState({ isTokenValid: true, loading: false, error: false });
        return;
      }

      // Force refresh token from backend
      token = await forceRefreshToken();
      if (token && verifyToken(token)) {
        setSession(token);
        setAuthState({ isTokenValid: true, loading: false, error: false });
      } else {
        setSession(null);
        setAuthState({ isTokenValid: false, loading: false, error: true });
      }
    } catch (err) {
      console.error('JWT init failed:', err);
      setSession(null);
      setAuthState({ isTokenValid: false, loading: false, error: true });
    }
  };

  useEffect(() => {
    initToken();
  }, [telegramToken]);

  if (authState.loading) {
    return <Loader />;
  }

  if (authState.error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
        <Paper elevation={3} sx={{ padding: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h3" gutterBottom>
            Cannot Access App
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We were unable to obtain a valid session from Telegram or connect to the game server. Please try reopening the mini-app.
          </Typography>
          <Button variant="contained" color="primary" onClick={initToken}>
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  return <JWTContext.Provider value={{ isTokenValid: authState.isTokenValid }}>{children}</JWTContext.Provider>;
};

export default JWTContext;
